import { createContext, useContext } from "react";

import type { Schemas } from "../lib/api/client";
import type { PlatformRole } from "./roles";

/**
 * The signed-in state of this browser, as the API reports it (P1-019).
 *
 * The session itself lives in an HttpOnly cookie that script never sees. What
 * is held here is what the API said about it, and one synchronizer token kept
 * in memory only (never in storage, cookies or the URL). The role and
 * permissions come from GET /v1/me/shops and decide only what the shell draws:
 * every request is authorized again by the server (ADR 0023). Frontend
 * visibility is not authorization.
 */
export type ShopAccess = {
  /** The caller's own tenant, which some requests name. */
  tenantId: string;
  shopId: string;
  roles: readonly string[];
  /** Role permissions and, since P5-013 W6, those of live delegated rights. */
  permissions: readonly string[];
  /** The rights an Admin gave this person in the shop (P5-013 W6). */
  delegatedRights: readonly string[];
};

export type WorkspaceSession =
  /** The first check with the API has not answered yet. */
  | { kind: "checking" }
  | { kind: "signed-out" }
  /** Past 30 minutes idle but inside 12 hours: no authority until signing in again. */
  | { kind: "idle-locked" }
  /** The API could not be reached, so nobody can say who is signed in. */
  | { kind: "unavailable" }
  | {
      kind: "signed-in";
      actorId: string;
      /** v1 has one shop per tenant; a user with several opens the first. */
      shop: ShopAccess | null;
      /** The role the shell draws for, from the shop's roles. */
      role: PlatformRole | null;
      /**
       * The business's theme and logo (P5-014), read with the session so the
       * shell draws in them from its first frame. Absent for platform staff,
       * and when it could not be read: the default theme is drawn instead.
       */
      branding?: Schemas["Branding"] | null;
      /**
       * The documents this person must still accept (P1-023). Anything in
       * the list means the shell draws only the acceptance screen. Absent
       * is the same as nothing outstanding.
       */
      legalOutstanding?: readonly Schemas["LegalRequirement"][];
      /** Everything the API requires, accepted or not, to compare versions with the bundle. */
      legalRequired?: readonly Schemas["LegalRequirement"][];
    };

export type SignInChallenge = {
  expiresAt: string;
  remainingAttempts: number;
  /** PASSKEY_REQUIRED: a Platform Superadmin with a passkey, whose code is refused (P1-025). */
  state: Schemas["SignInChallenge"]["state"];
  /** Present when the person holds a passkey: pass to the browser's get ceremony. */
  passkeyOptions?: Schemas["PasskeyRequestOptions"];
};

export type SessionContextValue = {
  session: WorkspaceSession;
  /** Password step. Resolves with the code challenge; rejects with ApiProblem. */
  startSignIn: (email: string, password: string) => Promise<SignInChallenge>;
  /**
   * Code step. Resolves null once the session is established and loaded, or
   * with the creation options when a Superadmin must first create a passkey.
   */
  completeSignIn: (code: string) => Promise<{ options: Schemas["PasskeyCreationOptions"]; expiresAt: string } | null>;
  /** Passkey step: the browser's assertion over the challenge from startSignIn. */
  completePasskeySignIn: (credential: Schemas["PasskeyAssertion"]) => Promise<void>;
  /** A Superadmin's first passkey, created after the code step. */
  completePasskeyEnrolment: (credential: Schemas["PasskeyAttestation"], name?: string) => Promise<void>;
  /** Idle unlock with the password alone (P1-024). Rejects with ApiProblem. */
  unlock: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Asks the API again, after it was unreachable or a request came back 401. */
  refresh: () => Promise<void>;
  /**
   * A signed-in request: carries the in-memory synchronizer token, and adopts
   * the rotated one when the response re-establishes the session (a password
   * change).
   */
  request: <T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: `/v1/${string}`,
    options?: { body?: unknown; reauthorizationId?: string; operationId?: string },
  ) => Promise<T>;
  /** Draws a newly saved branding at once, without asking the API again. */
  updateBranding: (branding: Schemas["Branding"]) => void;
  /** Records the API's full required list after an acceptance (P1-023). */
  updateLegal: (required: readonly Schemas["LegalRequirement"][]) => void;
  /** A signed-in POST whose answer is a file, such as a report download, with the synchronizer token. */
  download: (
    path: `/v1/${string}`,
    accept: string,
    options: { body?: unknown; reauthorizationId?: string; operationId?: string },
  ) => Promise<{ blob: Blob; filename: string | null }>;
};

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider.");
  }
  return context;
}

/** The strongest workspace role the user holds in the shop, if any. */
export function roleInShop(shop: ShopAccess | null): PlatformRole | null {
  for (const role of ["admin", "manager", "cashier"] as const) {
    if (shop?.roles.includes(role)) {
      return role;
    }
  }
  return null;
}

/** The role the shell draws for, or null when nobody is signed in. */
export function sessionRole(session: WorkspaceSession): PlatformRole | null {
  return session.kind === "signed-in" ? session.role : null;
}
